import React, { useState, useMemo, useRef } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { Segmented, Tooltip } from 'antd';
import ColorContainer from '../color-container';
import CardImage from './card-image';
import CardTerminal from './card-terminal';
import CardText from './card-text';
import CardCode from './card-code';
import CardSearch from './card-search';
import CardNewSearch from './card-new-search';
import TaskSearch from './task-search';
import CardVideo from './card-video';
import CardAudio from './card-audio';
import CardPdf from './card-pdf';
import CardPpt from './card-ppt';
import CardHtml from './card-html';
import MindMap from '@/components/vnc-viewer/mind-map'
import PlanQuestion from '../chat-history/plan-question';
import PlanSdk from '../chat-history/plan-sdk'
import { getMcpToolsConfig } from '@shared/constants/mcp';
import CardFiles from './card-files';
import copy from 'copy-to-clipboard';
import Other from '@/common/helper/other';
import { markdownToPlainText } from '@shared/utils';
import CardSandbox from './card-sandbox';
import CardMultipleTaskSearch from './card-multiple-task-search';
import { useEffect } from 'react';
import CardThinking from './card-thinking';
import CardMapSearch from './card-map-search';
import CardAnalysisTool from './card-analysis-tool';
import Code from './components/code';
import { McpItemContext } from '../chat-history/context';
import PlanViewer from '../chat-history/plane-viewer'
import { HelperCore } from '@/common/dependencies';
import { SVGPureIcon } from '@/ui';

const VncViewerCard = observer((props) => {
  const {
    item,
    currentCardId,
    mcpStore,
    newMsg,
  } = props;

  const { messageList, chatId } = mcpStore.chatStore || {};
  const {
    title, // 标题
    tool, // 工具类型
    data, // 数据
    url,
    event, // 透传过来的event
    errorMsg = "调用工具失败",
    msg_id,
    type
  } = item;

  const isAITyping = useMemo(() => {
    const isAITyping = mcpStore.chatStore.isAITyping && mcpStore.chatStore.lastMessage?.msg_id == msg_id;
    return isAITyping;
  }, [mcpStore.chatStore.isAITyping, mcpStore.chatStore.lastMessage?.msg_id, msg_id]);

  const messageItem = useMemo(() => {
    return messageList.find(item => item.msg_id == event.msg_id);
  }, [event.msg_id, messageList?.length]);

  const id = `card_${event?.msg_id || mcpStore.panelMessageId}_${event?.planStepId}`
  const [currentVal, setCurrentVal] = useState('result');
  const [currentDark, setCurrentDark] = useState(false);

  const toPngBlob = async (blob) => {
    const img = new Image();
    img.src = URL.createObjectURL(blob);
    return new Promise((resolve, reject) => {
      img.onload = async () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(pngBlob => {
            resolve(pngBlob)
          }, 'image/png');
      };
      img.onerror = reject;
    });
  }

  const copyFile = async (tool, text) => {
    if (tool === 'image' && /^https/.test(text)) {
      let blob = await fetch(text).then(res => res.blob()).catch(err => null);
      if (blob?.type === 'image/jpeg') {
        blob = await toPngBlob(blob).catch(err => null)
      }
      if (blob) {
        const data = [new ClipboardItem({
          [blob.type]: blob
        })];
        navigator.clipboard.write(data).then(() => {
          Other.toast('复制成功', "success");
        });
      }
    } else {
      if (tool === 'terminal') {
        text = text.replace(/<br ?\/?>/gi, '\n')
      }
      const plainText = markdownToPlainText(text);
      const copied = copy(plainText);
      copied && Other.toast('复制成功', "success");
    }
  }

  const config = getMcpToolsConfig(event);
  const [actionItems, setActionItems] = useState([]);
  const [paramsText, setParamsText] = useState("");
  
  // 反问和编辑规划任务
  const isPlanConfirm = tool === 'confirm' || tool == 'agentConfirm';
  const isChat = tool === 'chat';

  useEffect(() => {
    const actionItems = [
      {
        label: config.title || '结果',
        value: 'result',
      },
    ];
    const paramsText = mcpStore.getNodeParamsText(event);
    if(paramsText){
      actionItems.push({
        label: '工具参数',
        value: 'params',
      });
      setParamsText(paramsText);
    }
    if(config.key == "params"){
      actionItems.shift();
      setCurrentVal("params");
      setCurrentDark(true);
    }
    if(tool == "execute"){
      setCurrentDark(true);
    }
    setActionItems(actionItems);
  }, [event.status]);

  const onChangeTab = (value) => {
    setCurrentVal(value);
    if (value === 'params' || tool == "execute") {
      setCurrentDark(true);
    } else {
      setCurrentDark(false);
    }
  }

  // 子组件中的tab切换
  const onSubChangeTab = (value) => {
    if (value === '工具参数') {
      setCurrentDark(true);
    }else {
      setCurrentDark(false);
    }
  }

  const downloadFile = async (url, fileName) => {
    if (/^https/.test(url)) {
      let _fileName = fileName;
      // 如果fileName没有文件后缀，则从url里取
      if (!/(\.[a-z]{1,4})(?:$|\?)/gi.test(_fileName) && /(\.[a-z]{1,4})(?:$|\?)/gi.test(url)) {
        _fileName = fileName + RegExp.$1;
      }
      if (/qiniu/.test(url)) {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        if (!params.get('attname')) {
          params.set('attname', encodeURIComponent(_fileName))
        }
        HelperCore.saveFile(urlObj.href, _fileName)
      }
      else if (/xstore/.test(url)) {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        if (!params.get('response-content-disposition')) {
          params.set('response-content-disposition', 'attname');
          params.set('filename', encodeURIComponent(_fileName))
        }
        HelperCore.saveFile(urlObj.href, _fileName)
      }
      else {
        if(HelperCore.isWeixin()){
          HelperCore.saveFile(url, _fileName);
        }else{
          const blob = await fetch(url).then(res => res.blob()).catch(err => null);
          if (blob) {
            const url = URL.createObjectURL(blob);
            HelperCore.saveFile(url, _fileName);
            URL.revokeObjectURL(url);
          }
        }
        
      }
    }
  }

  const showHeader = !config.hiddenCommonTab && (config.showCommonTab || actionItems.length > 1 || (actionItems.length == 1 && actionItems[0].value == "params"))

  return (
    <McpItemContext.Provider value={{
      isAITyping,
    }}>
    <ColorContainer
      className={["mt-40px", newMsg ? 'msg-1' : 'msg-0']}
      // 只有当前卡片是正在运行的卡片时，才显示动画, 当终止会话时，动画不显示,  由于历史会话均不显示动画;
      animation={event.status == 'running' && isAITyping && event.msg_id === mcpStore.panelMessageId}
      dataId={id}
      dataTools={tool}
      dataStatus={event?.status}
      active={currentCardId === id}
    >
      <div className={[
        `bg-white flex flex-col tool-${tool} ${isChat ? '!pb-0px' : ''}`, 
        `${isPlanConfirm ? '' : 'card-height overflow-hidden'}`,
        currentDark ? 'bg-[#282c34]' : '',
        currentCardId === id ? 'rounded-10px' : 'rounded-11px',
        'cursor-pointer'
      ]}
        onClick={() => {
          mcpStore.setCurrentPanelId(event.planStepId, true);
          mcpStore.setPanelMessageId(event.msg_id);
        }}
      >
        {
          showHeader
          ? (
            <div className='px-24px py-16px flex justify-between items-center'>
              <div className='tabs' key={currentVal}>
                <Segmented
                  value={currentVal}
                  onChange={onChangeTab}
                  options={actionItems}
                />
              </div>

              <div className="flex-grow"></div>

              {
                tool === 'terminal' || tool === 'execute' || tool === 'image' ? (
                  <div
                    className={`
                      h-28px box-border rounded-6px border-1px border-black
                      border-opacity-16 leading-26px px-10px cursor-pointer
                      hover:bg-[rgba(0,0,0,0.03)] text-14px text-black
                      flex items-center ml-8px
                      ${HelperCore.isPhone() ? "border-0px px-0px" : ""}
                    `}
                    onClick={() => {
                      copyFile(tool, data)
                    }}
                  >
                    <SVGPureIcon
                      name="client_restore"
                      className="size-20px cursor-pointer mr-4px"
                    />
                    复制
                  </div>
                ) : null
              }

              {
                (((tool === 'image' || tool === 'video') && data) || (tool === 'audio' && data?.[0]?.url)) ? (
                  <div
                    className={`
                      h-28px box-border rounded-6px border-1px border-black
                      border-opacity-16 leading-26px px-10px cursor-pointer
                      hover:bg-[rgba(0,0,0,0.03)] text-14px text-black
                      flex items-center ml-8px
                      ${HelperCore.isPhone() ? "border-0px px-0px" : ""}
                    `}
                    onClick={e => {
                      let url = data;
                      let fileName = event?.title || event?.detail?.results?.[0].title;
                      if (tool === 'audio') {
                        url = data?.[0]?.url
                      }
                      downloadFile(url, fileName);
                    }}
                  >
                    <SVGPureIcon
                      name="download"
                      className="size-18px cursor-pointer mr-4px"
                    />
                    {
                      !HelperCore.isPhone()
                      ? "下载"
                      : null
                    }
                  </div>
              ) : null
              }
          </div>
          )
          : null
        }

        {
          currentVal == "params"
          ? (
            <div className={[
              "px-24px pr-16px mr-4px mb-16px whitespace-pre-wrap text-14px text-black  leading-24px break-all flex-shrink-1", 
              "overflow-y-scroll",
              currentDark ? "scrollbar-dark" : "scrollbar-light"
            ]}>
              <Code
                data={'```json\n' + (mcpStore.getNodeParamsText(event)?.trim() || "").replace(/\`/g, "'") + '\n```'}
                className="whitespace-pre-wrap"
                syntaxClassName="language-json"
                light={false}
                showLineNumbers={true}
              />
              {/* {mcpStore.getNodeParamsText(event)} */}
            </div>
          )
          : event?.status == "failed" && !config.hiddenCommonTab
          ? (
            <div className="flex flex-col h-400px items-center justify-center">
              <img 
                className='w-190px'
                src='https://p5.ssl.qhimg.com/t11098f6bcda6a604f3f799138d.png'
                />
              <div className="text-14px leading-30px text-black text-opacity-50">
                {errorMsg}
              </div>
            </div>
          )
          : (
            <>
              {
                // 图片
                tool === 'image' ? (
                  <CardImage
                    data={data}
                    event={event}
                    effect={event?.tool === 'browser_automation_task'}
                    position={event?.detail?.results?.[0]?.position}
                  />
                ) : null
              }

              {
                // terminal
                tool === 'terminal' ? <CardTerminal data={data} event={event} /> : null
              }

              { // text
                tool === 'text' ? (
                  <CardText data={data} url={url} event={event} cid={chatId} hasToolBar={showHeader ? true : false} />
                ) : null
              }

              {
                // code
                tool === 'execute' || tool === 'plan' ? (
                  <CardCode data={data} tool={tool} event={event} />
                ) : null
              }

              {
                // sandbox
                tool === 'sandbox' ? (
                  <CardSandbox event={event} mcpStore={mcpStore} currentDark={currentDark} callbackTabs={onSubChangeTab} />
                ) : null
              }

              {
                // 搜索
                tool === 'search' || tool === 'queryList' ? (
                  <CardSearch results={data} event={event} mcpStore={mcpStore} errorMsg={errorMsg} currentDark={currentDark} callbackTabs={onSubChangeTab} />
                ) : null
              }

              {
                // 搜索
                tool === 'new-search' ? (
                  <CardNewSearch results={data} event={event} mcpStore={mcpStore} errorMsg={errorMsg} currentDark={currentDark} callbackTabs={onSubChangeTab} />
                ) : null
              }

              {
                // 多任务搜索、图片
                tool === 'task_search' ? (
                  <TaskSearch
                    taskName={data?.taskName}
                    taskId={data?.taskId}
                    buType={event?.buType}
                  />
                ) : null
              }

              {
                // 视频
                tool === 'video' ? <CardVideo data={data} event={event} isAITyping={isAITyping} /> : null
              }

              {
                // 音频
                tool === 'audio' ? <CardAudio mcpStore={mcpStore} data={data} event={event} /> : null
              }

              {
                // pdf
                tool === 'pdf' ? <CardPdf url={data} event={event} cid={chatId} hasToolBar={paramsText ? true : false} /> : null
              }

              {
                // files
                tool === 'files' ? <CardFiles list={data} /> : null
              }

              {
                // ppt
                tool === 'html' && event.tool === 'gen_ppt_html' 
                ? <CardPpt data={data} url={url} event={event} mcpStore={mcpStore} />
                : null
              }

              {
                // html
                tool === 'html' && ((event.tool === 'gen_html' || event.type == "html") || event.type == "agentBlock") 
                ? <CardHtml data={data} url={url} event={event} mcpStore={mcpStore} />
                : null
              }
              
              {
                // mindmap
                tool === 'mindmap' ? <MindMap event={event} data={data} cid={chatId} title={mcpStore?.chatStore?.__historyData?.title || ""}/> : null
              }

              {
                // chat thinking
                isChat ? <CardThinking event={event} data={data} /> : null
              }

              {
                tool === 'confirm' ? (
                  <div className="pt-16px pb-24px">
                    <PlanQuestion
                      planData={{
                        ...event.detail,
                        title //拼接title
                      }}
                      planed={event.status == 'success' || !mcpStore.chatStore?.isOwner || !messageItem?.is_doing}
                      planStepId={event.planStepId}
                      requestPlanParams={{
                        id: event.planStepId,
                        message_id: event.msg_id,
                        conversation_id: event.conversation_id,
                      }}
                      event={event}
                    />
                  </div>
                ) : null
              }

              {
                // Agent 表单
                tool === 'agentConfirm' ? (
                  <div className="pt-16px pb-24px">
                    <PlanSdk 
                      planData={{
                        ...event.detail,
                        title //拼接title
                      }}
                      planed={event.status == 'success' || !mcpStore.chatStore?.isOwner || !messageItem?.is_doing}
                      planStepId={event.planStepId}
                      requestPlanParams={{
                        id: event.planStepId,
                        message_id: event.msg_id,
                        conversation_id: event.conversation_id,
                      }}
                    />
                  </div>
                ) : null
              }
              {
                tool === 'agentDyConfirm' ? (
                    <div className="pt-16px pb-24px">
                      <PlanViewer  
                        planData={{
                          ...event.detail,
                          title, //拼接title
                          content: event.content
                        }}
                        event={event} data={data}
                        messageItem={messageItem}
                        planed={event.status == 'success' || !mcpStore.chatStore?.isOwner || !messageItem?.is_doing}
                        planStepId={event.planStepId}
                        requestPlanParams={{
                          id: event.planStepId,
                          message_id: event.msg_id,
                          conversation_id: event.conversation_id,
                        }} 
                      />
                    </div>
                ) : null
              }
              {
                tool === 'gaokao_confirm' ? (
                  <div className="px-24px pt-16px pb-24px">
                    <PlanQuestion
                      planData={{
                        ...event.detail,
                        title //拼接title
                      }}
                      planed={event.status == 'success' || !mcpStore.chatStore?.isOwner || !messageItem?.is_doing}
                      planStepId={event.planStepId}
                      requestPlanParams={{
                        id: event.planStepId,
                        message_id: event.msg_id,
                        conversation_id: event.conversation_id,
                      }}
                    />
                  </div>
                ) : null
              }

              {/* 多任务搜索卡片 */}
              {
                tool === 'multiple_task' ? <CardMultipleTaskSearch event={event} data={event.detail?.results}/> : null
              }

              {
                tool === 'map_search' ? <CardMapSearch  event={event} mcpStore={mcpStore} errorMsg={errorMsg}/> : null
              }

              {
                tool === 'comprehensive_analysis_tool' ? <CardAnalysisTool data={data} /> : null
              }
            </>
          )
        }
      </div>
    </ColorContainer>
    </McpItemContext.Provider>
  )
});
export default VncViewerCard;